import clsx from "clsx";

export interface LevelRow {
  chainageM: number | "";
  groundM: number | "";
  waterM: number | "";
  tunnelM: number | "";
}

interface LevelsEditorProps {
  rows: LevelRow[];
  driveLengthM: number | null;
  onChange: (rows: LevelRow[]) => void;
}

/**
 * Edits the four parallel level arrays that the legacy code stored as
 * 11-row Ground/Water/Tunnel columns. We keep the chainage explicit per
 * row so non-uniform sampling works (e.g. dense around shaft eyes) and
 * the form doesn't lock you to 11 points.
 */
export function LevelsEditor({ rows, driveLengthM, onChange }: LevelsEditorProps) {
  const setCell = (i: number, key: keyof LevelRow, value: string) => {
    const next = rows.slice();
    const v = value === "" ? "" : Number(value);
    next[i] = { ...next[i]!, [key]: v as number | "" };
    onChange(next);
  };
  const addRow = () => {
    const last = rows[rows.length - 1];
    const ch =
      last && typeof last.chainageM === "number" && driveLengthM
        ? Math.min(driveLengthM, last.chainageM + 25)
        : 0;
    onChange([
      ...rows,
      { chainageM: ch, groundM: "", waterM: "", tunnelM: "" },
    ]);
  };
  const delRow = (i: number) => {
    if (rows.length <= 2) return;
    onChange(rows.filter((_, idx) => idx !== i));
  };

  return (
    <div className="inline-block rounded border border-border1 overflow-hidden max-w-full">
      <table className="text-[12px]">
        <thead className="bg-surface2">
          <tr className="text-[10.5px] text-dim">
            <Th>Chainage (m)</Th>
            <Th>Ground (m)</Th>
            <Th>Water (m)</Th>
            <Th>Mid level (m)</Th>
            <Th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border1">
              <Td>
                <NumCell
                  value={r.chainageM}
                  onChange={(v) => setCell(i, "chainageM", v)}
                  step="any"
                />
              </Td>
              <Td>
                <NumCell
                  value={r.groundM}
                  onChange={(v) => setCell(i, "groundM", v)}
                  step="any"
                />
              </Td>
              <Td>
                <NumCell
                  value={r.waterM}
                  onChange={(v) => setCell(i, "waterM", v)}
                  step="any"
                />
              </Td>
              <Td>
                <NumCell
                  value={r.tunnelM}
                  onChange={(v) => setCell(i, "tunnelM", v)}
                  step="any"
                />
              </Td>
              <Td>
                <button
                  type="button"
                  onClick={() => delRow(i)}
                  disabled={rows.length <= 2}
                  className={clsx(
                    "text-[11px] px-2 py-1 rounded",
                    rows.length <= 2
                      ? "text-dim cursor-not-allowed"
                      : "text-muted hover:text-wb",
                  )}
                >
                  ✕
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-3 py-2 border-t border-border1 bg-surface1">
        <button
          type="button"
          onClick={addRow}
          className="text-[11px] uppercase tracking-wider px-3 py-1 rounded border border-border2 text-text hover:border-wb hover:text-wb"
        >
          + Add level row
        </button>
      </div>
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return (
    <th className={clsx("text-left px-3 py-2 font-medium", className)}>
      {children}
    </th>
  );
}

function Td({ children }: { children?: React.ReactNode }) {
  return <td className="px-2 py-1.5">{children}</td>;
}

function NumCell({
  value,
  onChange,
  step,
}: {
  value: number | "";
  onChange: (v: string) => void;
  step?: string;
}) {
  return (
    <input
      type="number"
      step={step}
      value={value === "" ? "" : value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-bg border border-border1 rounded px-2 py-1 text-[12px] text-text focus:outline-none focus:border-wb"
    />
  );
}
